import { useState, useEffect } from 'react';
import { useLocation } from 'wouter';
import {
    Clock,
    Truck,
    AlertCircle,
    Filter,
    ArrowUpRight,
    ArrowDownRight,
    Search,
    Download,
    TrendingUp,
    Inbox,
} from 'lucide-react';
import { useAuthStore } from '../hooks/useAuth';
import { OperationsFeatureGate, isOperationsTierUser } from '../components/entitlements/OperationsFeatureGate';
import { api } from '../lib/api';
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts';
import type { DwellAnalyticsResponse } from '../types/analyticsDwell';
import { DwellAnalyticsExport } from '../components/reports/DwellAnalyticsExport';

interface KpiCardProps {
    title: string;
    value: string;
    trend: string;
    trendDir: 'up' | 'down';
    trendLabel: string;
    icon: React.ReactNode;
}

export function DwellAnalytics() {
    const [, setLocation] = useLocation();
    const [timeRange, setTimeRange] = useState('7d');
    const [data, setData] = useState<DwellAnalyticsResponse | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [showExport, setShowExport] = useState(false);
    const [warehouseName, setWarehouseName] = useState<string>('Warehouse');
    const { token, user } = useAuthStore();
    const isOperationsEntitled = isOperationsTierUser(user);

    // Fetch warehouse name once on mount (same pattern as EnvironmentalImpact)
    useEffect(() => {
        if (!token || !isOperationsEntitled) return;
        api.getWarehouses()
            .then(res => {
                const name = res.warehouses?.[0]?.name;
                if (name) setWarehouseName(name);
            })
            .catch(() => { /* keep default */ });
    }, [token]);

    useEffect(() => {
        fetchData();
    }, [timeRange, token]);

    const fetchData = async () => {
        if (!token || !isOperationsEntitled) return;
        setIsLoading(true);
        setError(null);

        try {
            const rangeDays = timeRange === '30d' ? 30 : timeRange === '14d' ? 14 : 7;
            const response = await fetch(
                `/api/analytics/dwell?rangeDays=${rangeDays}&_ts=${Date.now()}`,
                {
                    cache: 'no-store' as RequestCache,
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Cache-Control': 'no-cache',
                    },
                },
            );

            // 304 returns empty body — treat as transient error
            if (response.status === 304) {
                throw new Error('Stale cache (304) — please retry');
            }

            if (!response.ok) {
                throw new Error(`API error: ${response.status}`);
            }

            const json: DwellAnalyticsResponse = await response.json();
            setData(json);
        } catch (err: any) {
            console.error('Failed to load dwell analytics', err);
            setError(err.message || 'Failed to load data');
        } finally {
            setIsLoading(false);
        }
    };

    if (!isOperationsEntitled) {
        return (
            <div className="min-h-screen bg-slate-50 dark:bg-slate-950 p-6">
                <OperationsFeatureGate
                    featureName="Dwell & Detention Analytics"
                    description="Detailed dwell & gate-to-dock waiting analysis, detention exposure calculations, per-dock and per-carrier performance breakdowns, and printable dwell reports are included in the BayWhiz Operations plan ($199 USD/month per facility). Your Starter plan ($99 USD/month) includes live dock scheduling, check-in/checkout timestamps, and schedule CSV exports."
                    highlights={[
                        'Gate-to-dock waiting time and dock dwell duration trends',
                        'Automated detention exposure & cost estimation by carrier and dock door',
                        'Printable executive dwell & detention performance reports',
                    ]}
                    testId="dwell-analytics-operations-gate"
                />
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex items-center justify-center">
                <div className="flex flex-col items-center gap-4 text-center">
                    <AlertCircle className="w-12 h-12 text-red-500" />
                    <p className="text-slate-500 dark:text-slate-400 text-sm">{error}</p>
                    <button onClick={fetchData} className="px-4 py-2 bg-cyan-600 text-white rounded-lg text-sm font-bold hover:bg-cyan-700 transition-colors">
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    if (isLoading || !data) {
        return (
            <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex items-center justify-center">
                <div className="flex flex-col items-center gap-4">
                    <div className="w-12 h-12 border-4 border-slate-200 dark:border-slate-800 border-t-cyan-500 rounded-full animate-spin"></div>
                    <p className="text-slate-500 dark:text-slate-400 font-mono text-sm tracking-widest uppercase">
                        Analyzing Dwell Times...
                    </p>
                </div>
            </div>
        );
    }

    const kpis = data.kpis ?? { avgDwellMin: 0, avgWaitMin: 0, detentionRatePct: 0, estDetentionCost: 0 };
    const trend = data.trend ?? [];
    const dockPerformance = data.dockPerformance ?? [];
    const carrierLeaderboard = data.carrierLeaderboard ?? [];
    const detentionGrace = data.detentionGraceMinutes ?? data.graceMinutes ?? 60;
    const meta = data._meta ?? {
        computedAt: Date.now(),
        rangeDays: data.rangeDays ?? 7,
        detentionGraceMinutesUsed: detentionGrace,
        hourlyRateUsed: 0,
        loadsWithDwell: 0,
    };
    const graceDisplay = meta.detentionGraceMinutesUsed ?? meta.graceMinutesUsed ?? detentionGrace;

    // Compute calendar date range for the export document
    const exportRangeDays = meta.rangeDays ?? 7;
    const rangeLabel = exportRangeDays === 30 ? 'Last 30 Days' : exportRangeDays === 14 ? 'Last 14 Days' : 'Last 7 Days';
    const exportEndDate = new Date();
    exportEndDate.setHours(23, 59, 59, 0);
    const exportStartDate = new Date(exportEndDate);
    exportStartDate.setDate(exportStartDate.getDate() - exportRangeDays + 1);
    exportStartDate.setHours(0, 0, 0, 0);

    return (
        <>
        <div className="min-h-screen bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-slate-50 p-6 lg:p-8 font-sans">

            {/* Header */}
            <header className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-8 pl-12 md:pl-0">
                <div>
                    <h1 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-slate-50 flex flex-wrap items-center gap-3">
                        Dwell & Detention Analysis
                        <span className="px-2 py-1 rounded bg-slate-200 dark:bg-slate-800 text-xs font-mono text-slate-600 dark:text-slate-400">
                            Detention Free Time: {graceDisplay}m
                        </span>
                    </h1>
                    <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                        Optimize dock turnaround and reduce detention costs
                    </p>
                </div>

                <div className="flex items-center gap-3">
                    <div className="hidden md:flex items-center bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg p-1">
                        {['7d', '14d', '30d'].map((range) => (
                            <button
                                key={range}
                                onClick={() => setTimeRange(range)}
                                className={`px-4 py-1.5 rounded-md text-xs font-bold transition-colors ${timeRange === range
                                    ? 'bg-cyan-600 text-white shadow-sm'
                                    : 'text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200'
                                    }`}
                            >
                                {range === '7d' ? '7 Days' : range === '14d' ? '14 Days' : '30 Days'}
                            </button>
                        ))}
                    </div>
                    <button
                        onClick={() => setShowExport(true)}
                        className="flex items-center gap-2 px-4 py-2 bg-slate-900 dark:bg-slate-50 text-white dark:text-slate-900 rounded-lg text-sm font-bold shadow-sm hover:bg-slate-800 dark:hover:bg-slate-200 transition-colors"
                    >
                        <Download className="w-4 h-4" />
                        <span className="hidden sm:inline">Export Report</span>
                    </button>
                </div>
            </header>

            {/* Metadata */}
            <div className="flex flex-wrap items-center gap-4 mb-4 text-[11px] text-slate-400 dark:text-slate-500 font-mono">
                <span>Updated: {new Date(Number(meta.computedAt)).toLocaleTimeString()}</span>
                <span>·</span>
                <span>Loads: {meta.loadsWithDwell}</span>
                <span>·</span>
                <span>Grace: {meta.graceMinutesUsed}m</span>
                <span>·</span>
                <span>Rate: ${meta.hourlyRateUsed}/hr</span>
            </div>

            {/* KPIs */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                <KpiCard
                    title="Avg Dwell Time"
                    value={`${kpis.avgDwellMin}m`}
                    trend={kpis.avgDwellMin > graceDisplay ? 'Over' : 'Under'}
                    trendDir={kpis.avgDwellMin > graceDisplay ? 'up' : 'down'}
                    trendLabel={`vs Grace (${graceDisplay}m)`}
                    icon={<Clock className="w-5 h-5 text-blue-500" />}
                />
                <KpiCard
                    title="Avg Wait Time"
                    value={`${kpis.avgWaitMin}m`}
                    trend={kpis.avgWaitMin > 15 ? 'High' : 'Normal'}
                    trendDir={kpis.avgWaitMin > 15 ? 'up' : 'down'}
                    trendLabel="gate to dock"
                    icon={<Truck className="w-5 h-5 text-amber-500" />}
                />
                <KpiCard
                    title="Detention Rate"
                    value={`${kpis.detentionRatePct}%`}
                    trend={kpis.detentionRatePct > 10 ? 'High' : 'Low'}
                    trendDir={kpis.detentionRatePct > 10 ? 'up' : 'down'}
                    trendLabel="of total loads"
                    icon={<AlertCircle className="w-5 h-5 text-red-500" />}
                />
                <KpiCard
                    title="Est. Detention Cost"
                    value={`$${kpis.estDetentionCost.toLocaleString()}`}
                    trend={kpis.estDetentionCost > 0 ? `$${kpis.estDetentionCost}` : '$0'}
                    trendDir={kpis.estDetentionCost > 0 ? 'up' : 'down'}
                    trendLabel="this period"
                    icon={<TrendingUp className="w-5 h-5 text-emerald-500" />}
                />
            </div>

            {/* Main Content Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">

                {/* Chart Section */}
                <div className="lg:col-span-2 bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 p-6 shadow-sm">
                    <div className="flex items-center justify-between mb-6">
                        <div>
                            <h3 className="text-lg font-bold text-slate-900 dark:text-slate-50">Dwell Time Trends</h3>
                            <p className="text-sm text-slate-500 dark:text-slate-400">Daily average dwell minutes</p>
                        </div>
                        <div className="flex gap-2">
                            <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
                                <span className="w-3 h-3 bg-cyan-500 rounded-sm"></span> Dwell
                            </div>
                        </div>
                    </div>
                    {trend.some(t => t.loadCount > 0) ? (
                        <div className="h-[320px] w-full">
                            <ResponsiveContainer width="100%" height="100%">
                                <AreaChart data={trend.map(t => ({ date: t.date.slice(5), avgDwell: t.avgDwellMin }))} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                                    <defs>
                                        <linearGradient id="dwellGradient" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="0%" stopColor="#06b6d4" stopOpacity={0.25} />
                                            <stop offset="95%" stopColor="#06b6d4" stopOpacity={0.02} />
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" className="dark:stroke-slate-700/50" vertical={false} />
                                    <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                                    <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} domain={['auto', 'auto']} unit="m" />
                                    <Tooltip
                                        contentStyle={{ background: '#1e293b', border: 'none', borderRadius: 8, fontSize: 13, color: '#f1f5f9', boxShadow: '0 4px 12px rgba(0,0,0,.3)' }}
                                        itemStyle={{ color: '#06b6d4' }}
                                        labelStyle={{ color: '#94a3b8', marginBottom: 4 }}
                                        formatter={(value: number | undefined) => {
                                            const v = value ?? 0;
                                            return [`${v > 0 && v < 1 ? Number(v.toFixed(1)) : Math.round(v)}m`, 'Avg Dwell'];
                                        }}
                                    />
                                    <Area type="monotone" dataKey="avgDwell" stroke="#06b6d4" strokeWidth={3} fill="url(#dwellGradient)" dot={{ r: 4, fill: '#06b6d4', strokeWidth: 0 }} activeDot={{ r: 5, strokeWidth: 2, fill: '#06b6d4' }} />
                                </AreaChart>
                            </ResponsiveContainer>
                        </div>
                    ) : (
                        <EmptyState message="No dwell data for this period" />
                    )}
                </div>

                {/* Dock Performance */}
                <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 p-6 shadow-sm flex flex-col">
                    <div className="mb-4">
                        <h3 className="text-lg font-bold text-slate-900 dark:text-slate-50">Dock Performance</h3>
                        <p className="text-sm text-slate-500 dark:text-slate-400">Worst performing docks by dwell</p>
                    </div>
                    <div className="flex-1 overflow-auto">
                        {dockPerformance.length > 0 ? (
                            <div className="space-y-3">
                                {dockPerformance.map((dock) => (
                                    <div key={dock.dockId} className="p-3 rounded-lg bg-slate-50 dark:bg-slate-950 border border-slate-100 dark:border-slate-800 flex items-center justify-between">
                                        <div className="flex items-center gap-3">
                                            <div className="w-8 h-8 rounded bg-slate-200 dark:bg-slate-800 flex items-center justify-center font-mono font-bold text-slate-600 dark:text-slate-300 text-xs">
                                                {dock.dockLabel}
                                            </div>
                                            <div>
                                                <div className="text-sm font-bold text-slate-900 dark:text-slate-200">{dock.dockLabel}</div>
                                                <div className="text-xs text-slate-500 dark:text-slate-400">{dock.loadCount} loads • {dock.overGracePct}% over grace</div>
                                            </div>
                                        </div>
                                        <div className="text-right">
                                            <div className={`text-sm font-bold ${dock.avgDwellMin > 90 ? 'text-red-600 dark:text-red-400' :
                                                dock.avgDwellMin > 60 ? 'text-amber-600 dark:text-amber-400' :
                                                    'text-emerald-600 dark:text-emerald-400'
                                                }`}>
                                                {dock.avgDwellMin}m
                                            </div>
                                            <div className="text-[10px] text-slate-400 uppercase">Avg Dwell</div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <EmptyState message="No dock data available" />
                        )}
                    </div>
                </div>
            </div>

            {/* Carrier Leaderboard */}
            <div className="mt-8 bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 overflow-hidden shadow-sm">
                <div className="p-6 border-b border-slate-100 dark:border-slate-800 flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div>
                        <h3 className="text-lg font-bold text-slate-900 dark:text-slate-50">Carrier Detention Leaderboard</h3>
                        <p className="text-sm text-slate-500 dark:text-slate-400">Identify carriers contributing to high dwell times</p>
                    </div>
                    <div className="flex gap-2 w-full md:w-auto">
                        <div className="relative flex-1 md:flex-initial">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                            <input
                                type="text"
                                placeholder="Search carriers..."
                                className="w-full md:w-64 pl-9 pr-4 py-2 h-9 text-sm bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-lg text-slate-900 dark:text-slate-50 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                            />
                        </div>
                        <button className="p-2 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-lg text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 shrink-0">
                            <Filter className="w-4 h-4" />
                        </button>
                    </div>
                </div>
                {carrierLeaderboard.length > 0 ? (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm text-left">
                            <thead className="bg-slate-50 dark:bg-slate-950 text-xs uppercase tracking-wider text-slate-500 dark:text-slate-400 font-semibold border-b border-slate-200 dark:border-slate-800">
                                <tr>
                                    <th className="px-6 py-4">Carrier Name</th>
                                    <th className="px-6 py-4 text-center">Total Loads</th>
                                    <th className="px-6 py-4 text-center">Avg Dwell</th>
                                    <th className="px-6 py-4 text-center">Detention Violations</th>
                                    <th className="px-6 py-4 text-right">Status</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                                {carrierLeaderboard.map((carrier) => (
                                    <tr key={carrier.carrierKey} className="group hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors">
                                        <td className="px-6 py-4 font-bold text-slate-900 dark:text-slate-200">
                                            {carrier.carrierName}
                                        </td>
                                        <td className="px-6 py-4 text-center text-slate-600 dark:text-slate-400">
                                            {carrier.totalLoads}
                                        </td>
                                        <td className="px-6 py-4 text-center">
                                            <span className={`font-mono font-bold ${carrier.avgDwellMin > 90 ? 'text-red-600 dark:text-red-400' :
                                                carrier.avgDwellMin > 60 ? 'text-amber-600 dark:text-amber-400' :
                                                    'text-slate-600 dark:text-slate-400'
                                                }`}>
                                                {carrier.avgDwellMin}m
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 text-center">
                                            {carrier.detentionViolations > 0 ? (
                                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 dark:bg-red-950 text-red-800 dark:text-red-400 border border-red-200 dark:border-red-800">
                                                    {carrier.detentionViolations} Incidents
                                                </span>
                                            ) : (
                                                <span className="text-slate-400">-</span>
                                            )}
                                        </td>
                                        <td className="px-6 py-4 text-right">
                                            <button
                                                className="text-xs font-bold text-cyan-600 dark:text-cyan-400 hover:underline"
                                                onClick={() => setLocation(`/carriers?highlight=${encodeURIComponent(carrier.carrierKey)}`)}
                                            >
                                                View History
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div className="p-12">
                        <EmptyState message="No carrier data for this period" />
                    </div>
                )}
            </div>
        </div>

            {/* Export modal — mounts only when triggered */}
            {showExport && data && (
                <DwellAnalyticsExport
                    kpis={kpis}
                    trend={trend}
                    dockPerformance={dockPerformance}
                    carrierLeaderboard={carrierLeaderboard}
                    graceMinutes={graceDisplay}
                    hourlyRate={meta.hourlyRateUsed}
                    loadsWithDwell={meta.loadsWithDwell}
                    computedAt={Number(meta.computedAt)}
                    rangeLabel={rangeLabel}
                    startDate={exportStartDate}
                    endDate={exportEndDate}
                    facilityName={warehouseName}
                    onClose={() => setShowExport(false)}
                />
            )}
        </>
    );
}

// Empty state component
function EmptyState({ message }: { message: string }) {
    return (
        <div className="flex flex-col items-center justify-center py-12 text-center">
            <Inbox className="w-10 h-10 text-slate-300 dark:text-slate-700 mb-3" />
            <p className="text-sm text-slate-400 dark:text-slate-500 font-medium">{message}</p>
        </div>
    );
}

// Solid KPI Card (No opacity, no blur)
function KpiCard({ title, value, trend, trendDir, trendLabel, icon }: KpiCardProps) {
    const isBad = (trendDir === 'up' && (title.includes('Dwell') || title.includes('Detention'))) || (trendDir === 'down' && title.includes('Throughput'));
    const isGood = !isBad;

    return (
        <div className="bg-white dark:bg-slate-900 p-5 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm flex flex-col justify-between hover:border-slate-300 dark:hover:border-slate-700 transition-colors group">
            <div className="flex justify-between items-start mb-2">
                <h3 className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">{title}</h3>
                <span className="p-2 bg-slate-50 dark:bg-slate-800 rounded-lg text-slate-400 group-hover:text-slate-900 dark:group-hover:text-slate-200 transition-colors">
                    {icon}
                </span>
            </div>
            <div>
                <div className="text-2xl font-bold text-slate-900 dark:text-slate-50 tracking-tight">{value}</div>
                <div className="flex items-center gap-2 mt-1">
                    <span className={`text-xs font-bold flex items-center ${isGood ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'
                        }`}>
                        {trendDir === 'up' ? <ArrowUpRight className="w-3 h-3 mr-0.5" /> : <ArrowDownRight className="w-3 h-3 mr-0.5" />}
                        {trend}
                    </span>
                    <span className="text-xs text-slate-400">{trendLabel}</span>
                </div>
            </div>
        </div>
    );
}
